import { useContext, useState } from "react";

import { useTranslation } from "react-i18next";

import { AxiosError } from "axios";
import { css } from "@emotion/react";

import { OtpInputField } from "components/Auth/shared/OtpInputField";
import Button, {
    BUTTON_COLORS,
    BUTTON_SIZES,
    BUTTON_VARIANTS
} from "components/DesignComponents/Button";
import Dialog from "components/DesignComponents/Dialog";
import AuthContext from "context/auth/authContext";
import { useApi, useMediaQueries } from "shared/hooks";
import { maxDialogWidth } from "shared/styles";
import { DialogHandler, ENDPOINTS } from "shared/types";

import { RecoveryCodes } from "../../TwoFactorAuthentication/components/RecoveryCodes";
import { RECOVERY_CODE_STEPS } from "./types";

const TwoFactorRecoveryCodesModal = ({ isOpen, close }: DialogHandler) => {
    const { authType } = useContext(AuthContext);

    const { t } = useTranslation();
    const { fromMd } = useMediaQueries();
    const { postData, handleResponse } = useApi();

    const [step, setStep] = useState(RECOVERY_CODE_STEPS.SendOtpCode);
    const [isLoading, setIsLoading] = useState(false);
    const [otpCode, setOtpCode] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [recoveryCodes, setRecoveryCodes] = useState([]);
    const [isRecoveryCodesSaved, setIsRecoveryCodesSaved] = useState(false);

    const handleStepChange = (newStep: RECOVERY_CODE_STEPS) => setStep(newStep);

    const handleOtpChange = (newValue: string) => {
        setError(null);
        setOtpCode(newValue);
    };

    const handleSubmit = async () => {
        try {
            setIsLoading(true);

            const { data } = await postData(
                ENDPOINTS.TfaGenerateRecoveryCodes,
                {
                    otp_code: otpCode,
                    two_factor_auth_type: authType
                }
            );

            setRecoveryCodes(data.recovery_codes);
            handleStepChange(RECOVERY_CODE_STEPS.SaveRecoveryCodes);
            setOtpCode("");
        } catch (error) {
            const err = error as AxiosError;

            if (err.response?.status === 400) {
                setError(t("Security##2FA##send otp code##incorrect code"));
            } else {
                handleResponse(error);
            }
        }

        setIsLoading(false);
    };

    const steps = {
        [RECOVERY_CODE_STEPS.SendOtpCode]: {
            title: t("Security##recovery codes##first step title"),
            description: t("Security##recovery codes##first step description"),
            primaryBtnText: t("Button##generate codes"),
            isSubmitBtnEnabled: otpCode.length >= 6,
            content: (
                <OtpInputField
                    isLoading={isLoading}
                    error={error}
                    onChange={handleOtpChange}
                    label={t("Security##2FA##send otp code##six digit code")}
                    isRequired
                />
            ),
            onPrimaryBtnClick: handleSubmit
        },
        [RECOVERY_CODE_STEPS.SaveRecoveryCodes]: {
            title: t("Security##recovery codes##second step title"),
            description: t("Security##recovery codes##second step description"),
            primaryBtnText: t("Button##finish"),
            isSubmitBtnEnabled: isRecoveryCodesSaved,
            content: (
                <RecoveryCodes
                    codes={recoveryCodes}
                    onCheck={setIsRecoveryCodesSaved}
                />
            ),
            onPrimaryBtnClick: close
        }
    };

    const title = steps[step].title;
    const description = steps[step].description;
    const primaryBtnText = steps[step].primaryBtnText;
    const isSubmitBtnEnabled = steps[step].isSubmitBtnEnabled;
    const content = steps[step].content;
    const onPrimaryBtnClick = steps[step].onPrimaryBtnClick;

    const isSendOtpStep = step === RECOVERY_CODE_STEPS.SendOtpCode;

    return (
        <Dialog
            open={isOpen}
            close={isLoading || !isSendOtpStep ? undefined : close}
            isTitleSeparator
            isActionsSeparator
            title={title}
            description={description}
            submit={onPrimaryBtnClick}
            slotProps={{
                transition: {
                    onExited: () => {
                        setStep(RECOVERY_CODE_STEPS.SendOtpCode);
                        setOtpCode("");
                        setError(null);
                        setRecoveryCodes([]);
                        setIsRecoveryCodesSaved(false);
                    }
                }
            }}
            actions={
                <>
                    {isSendOtpStep && (
                        <Button
                            fullWidth
                            color={BUTTON_COLORS.Secondary}
                            size={BUTTON_SIZES.Normal}
                            variant={BUTTON_VARIANTS.TextOnly}
                            disabled={isLoading}
                            onClick={close}
                        >
                            {t("Button##close")}
                        </Button>
                    )}

                    <Button
                        fullWidth
                        color={BUTTON_COLORS.Primary}
                        size={BUTTON_SIZES.Normal}
                        variant={BUTTON_VARIANTS.TextOnly}
                        type="submit"
                        isLoading={isLoading}
                        disabled={!isSubmitBtnEnabled}
                    >
                        {primaryBtnText}
                    </Button>
                </>
            }
            css={css(isSendOtpStep && fromMd && maxDialogWidth)}
        >
            {content}
        </Dialog>
    );
};

export default TwoFactorRecoveryCodesModal;
